import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProducts, updateProduct, deleteProduct, getCategories, updateCategoryImage, toggleProductAvailability, getSearchConfig, updateSearchConfig, getHeroBlocks, updateHeroBlock, getAllSearchRecommendations, createSearchRecommendation, updateSearchRecommendation, deleteSearchRecommendation, getAllLandingConfigs, createLandingConfig, updateLandingConfig, deleteLandingConfig, getAllPortadas, updatePortada, getLogo, updateLogo } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Package, Image as ImageIcon, DollarSign, LogOut, Save, Trash2, Loader2, Layers, ToggleLeft, SearchIcon, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboardNew = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchConfig, setSearchConfig] = useState(null);
  const [heroBlocks, setHeroBlocks] = useState([]);
  const [searchRecommendations, setSearchRecommendations] = useState([]);
  const [landingConfigs, setLandingConfigs] = useState([]);
  const [portadas, setPortadas] = useState([]);
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingHeroBlock, setEditingHeroBlock] = useState(null);
  const [editingRecommendation, setEditingRecommendation] = useState(null);
  const [editingLandingConfig, setEditingLandingConfig] = useState(null);
  const [editingPortada, setEditingPortada] = useState(null);
  const [editingLogo, setEditingLogo] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [heroDialogOpen, setHeroDialogOpen] = useState(false);
  const [recommendationDialogOpen, setRecommendationDialogOpen] = useState(false);
  const [landingConfigDialogOpen, setLandingConfigDialogOpen] = useState(false);
  const [portadaDialogOpen, setPortadaDialogOpen] = useState(false);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      // Use Promise.allSettled to handle individual API failures gracefully
      const results = await Promise.allSettled([
        getProducts(),
        getCategories(),
        getSearchConfig(),
        getHeroBlocks(),
        getAllSearchRecommendations(),
        getAllLandingConfigs(),
        getAllPortadas(),
        getLogo()
      ]);
      
      // Extract data from settled promises, using empty arrays/objects for failures
      const [productsResult, categoriesResult, searchResult, heroResult, recommendationsResult, landingConfigsResult, portadasResult, logoResult] = results;
      
      if (productsResult.status === 'fulfilled') setProducts(productsResult.value);
      if (categoriesResult.status === 'fulfilled') setCategories(categoriesResult.value.filter(c => c.id !== 'todos'));
      if (searchResult.status === 'fulfilled') setSearchConfig(searchResult.value);
      if (heroResult.status === 'fulfilled') setHeroBlocks(heroResult.value);
      if (recommendationsResult.status === 'fulfilled') setSearchRecommendations(recommendationsResult.value);
      if (landingConfigsResult.status === 'fulfilled') setLandingConfigs(landingConfigsResult.value);
      if (portadasResult.status === 'fulfilled') setPortadas(portadasResult.value);
      if (logoResult.status === 'fulfilled') setLogo(logoResult.value);
      
      // Check if any critical APIs failed
      const failedApis = results.filter(r => r.status === 'rejected');
      if (failedApis.length > 0) {
        console.warn('Some APIs failed to load:', failedApis);
      }
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    toast.success('Sesión cerrada');
    navigate('/admin');
  };

  const handleEdit = (product) => {
    setEditingProduct({ ...product });
    setEditDialogOpen(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory({ ...category });
    setCategoryDialogOpen(true);
  };

  const handleSaveProduct = async () => {
    try {
      await updateProduct(editingProduct.id, {
        name: editingProduct.name,
        price: editingProduct.price,
        monthly_payment: editingProduct.monthly_payment,
        image: editingProduct.image,
        description: editingProduct.description
      });
      
      toast.success('Producto actualizado exitosamente');
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error actualizando producto');
    }
  };

  const handleSaveCategory = async () => {
    try {
      console.log('Saving category:', editingCategory.id, editingCategory);
      const response = await updateCategoryImage(editingCategory.id, {
        image: editingCategory.image,
        heroImage: editingCategory.heroImage,
        headline: editingCategory.headline,
        subtitle: editingCategory.subtitle
      });
      console.log('Category update response:', response);
      toast.success('Categoría actualizada exitosamente');
      setCategoryDialogOpen(false);
      await fetchData();
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error(`Error actualizando categoría: ${error.message || 'Desconocido'}`);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;

    try {
      await deleteProduct(productId);
      toast.success('Producto eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error eliminando producto');
    }
  };

  const handleToggleAvailability = async (productId, currentAvailability) => {
    try {
      await toggleProductAvailability(productId, !currentAvailability);
      toast.success(
        !currentAvailability 
          ? 'Producto marcado como disponible' 
          : 'Producto marcado como fuera de servicio'
      );
      fetchData();
    } catch (error) {
      toast.error('Error actualizando disponibilidad');
    }
  };

  const handleSaveSearchConfig = async () => {
    try {
      await updateSearchConfig(searchConfig);
      toast.success('Configuración de búsqueda actualizada');
      fetchData();
    } catch (error) {
      toast.error('Error actualizando configuración');
    }
  };

  const handleEditHeroBlock = (block) => {
    setEditingHeroBlock({ ...block });
    setHeroDialogOpen(true);
  };

  const handleSaveHeroBlock = async () => {
    try {
      await updateHeroBlock(editingHeroBlock.id, editingHeroBlock);
      toast.success('Bloque hero actualizado exitosamente');
      setHeroDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error actualizando bloque hero');
    }
  };

  const handleEditRecommendation = (recommendation) => {
    setEditingRecommendation({ ...recommendation });
    setRecommendationDialogOpen(true);
  };

  const handleCreateRecommendation = () => {
    setEditingRecommendation({
      id: `rec_${Date.now()}`,
      name: '',
      imageUrl: '',
      destination: '',
      active: true,
      order: searchRecommendations.length + 1,
      title: '',
      subtitle: '',
      heroImage: '',
      productImage: '',
      backgroundColor: '#f5f5f7',
      ctaBuyText: 'Comprar ahora',
      ctaBuyLink: '/catalog',
      ctaInfoText: 'Más información',
      ctaInfoLink: '/catalog'
    });
    setRecommendationDialogOpen(true);
  };

  const handleSaveRecommendation = async () => {
    try {
      const existingRecommendation = searchRecommendations.find(r => r.id === editingRecommendation.id);
      
      if (existingRecommendation) {
        await updateSearchRecommendation(editingRecommendation.id, editingRecommendation);
        toast.success('Recomendación actualizada exitosamente');
      } else {
        await createSearchRecommendation(editingRecommendation);
        toast.success('Recomendación creada exitosamente');
      }
      
      setRecommendationDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error guardando recomendación');
    }
  };

  const handleDeleteRecommendation = async (recommendationId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta recomendación?')) return;
    
    try {
      await deleteSearchRecommendation(recommendationId);
      toast.success('Recomendación eliminada exitosamente');
      fetchData();
    } catch (error) {
      toast.error('Error eliminando recomendación');
    }
  };

  // Landing Config handlers
  const handleEditLandingConfig = (config) => {
    setEditingLandingConfig({ ...config });
    setLandingConfigDialogOpen(true);
  };

  const handleCreateLandingConfig = () => {
    setEditingLandingConfig({
      id: `landing_${Date.now()}`,
      name: '',
      title: '',
      subtitle: '',
      price: 0,
      monthlyPrice: 0,
      backgroundImage: '',
      productImage: '',
      active: true,
      productId: ''
    });
    setLandingConfigDialogOpen(true);
  };

  const handleSaveLandingConfig = async () => {
    try {
      const existingConfig = landingConfigs.find(c => c.id === editingLandingConfig.id);
      
      if (existingConfig) {
        await updateLandingConfig(editingLandingConfig.id, editingLandingConfig);
        toast.success('Configuración actualizada exitosamente');
      } else {
        await createLandingConfig(editingLandingConfig);
        toast.success('Configuración creada exitosamente');
      }
      
      setLandingConfigDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error guardando configuración');
    }
  };

  const handleDeleteLandingConfig = async (configId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta configuración?')) return;
    
    try {
      await deleteLandingConfig(configId);
      toast.success('Configuración eliminada exitosamente');
      fetchData();
    } catch (error) {
      toast.error('Error eliminando configuración');
    }
  };

  // Portada handlers
  const handleEditPortada = (portada) => {
    setEditingPortada({ ...portada });
    setPortadaDialogOpen(true);
  };

  const handleSavePortada = async () => {
    try {
      await updatePortada(editingPortada.id, editingPortada);
      toast.success('Portada actualizada exitosamente');
      setPortadaDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error guardando portada');
    }
  };

  // Logo handlers
  const handleEditLogo = () => {
    setEditingLogo({ ...logo });
    setLogoDialogOpen(true);
  };

  const handleSaveLogo = async () => {
    try {
      await updateLogo(editingLogo);
      toast.success('Logo actualizado exitosamente');
      setLogoDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Error guardando logo');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/95 backdrop-blur">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <h1 className="text-xl font-bold">Panel de Administración - NAGOC GROUP</h1>
          <Button variant="ghost" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            Cerrar sesión
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="grid w-full max-w-5xl grid-cols-7">
            <TabsTrigger value="products">
              <Package className="h-4 w-4 mr-2" />
              Productos
            </TabsTrigger>
            <TabsTrigger value="images">
              <ImageIcon className="h-4 w-4 mr-2" />
              Imágenes
            </TabsTrigger>
            <TabsTrigger value="prices">
              <DollarSign className="h-4 w-4 mr-2" />
              Precios
            </TabsTrigger>
            <TabsTrigger value="catalog">
              <Layers className="h-4 w-4 mr-2" />
              Catálogo
            </TabsTrigger>
            <TabsTrigger value="availability">
              <ToggleLeft className="h-4 w-4 mr-2" />
              Disponibilidad
            </TabsTrigger>
            <TabsTrigger value="search">
              <SearchIcon className="h-4 w-4 mr-2" />
              Búsqueda
            </TabsTrigger>
            <TabsTrigger value="fondos">
              <ImagePlus className="h-4 w-4 mr-2" />
              Fondos
            </TabsTrigger>
            <TabsTrigger value="portadas">
              <Layers className="h-4 w-4 mr-2" />
              Portadas
            </TabsTrigger>
            <TabsTrigger value="logo">
              <ImageIcon className="h-4 w-4 mr-2" />
              Logo
            </TabsTrigger>
          </TabsList>

          {/* Products Tab */}
          <TabsContent value="products" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Gestión de Productos ({products.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="w-16 h-16 flex items-center justify-center bg-white rounded p-1">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-contain object-center"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{product.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          RD${product.price.toLocaleString()} | Cuota: RD${product.monthly_payment.toLocaleString()}/mes
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(product)}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Images Tab */}
          <TabsContent value="images">
            <Card>
              <CardHeader>
                <CardTitle>Gestión de Imágenes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {products.map((product) => (
                    <div key={product.id} className="space-y-2">
                      <div className="w-full aspect-square flex items-center justify-center bg-white rounded-lg p-2">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-contain object-center"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground truncate">
                        {product.name}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => handleEdit(product)}
                      >
                        Cambiar imagen
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Prices Tab */}
          <TabsContent value="prices">
            <Card>
              <CardHeader>
                <CardTitle>Gestión de Precios y Cuotas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <span className="font-medium">{product.name}</span>
                      <div className="flex gap-4">
                        <span className="text-sm">RD${product.price.toLocaleString()}</span>
                        <span className="text-sm text-muted-foreground">
                          RD${product.monthly_payment.toLocaleString()}/mes
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(product)}
                        >
                          Editar
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NEW: Catalog Tab */}
          <TabsContent value="catalog">
            <Card>
              <CardHeader>
                <CardTitle>Gestión de Catálogo (Categorías)</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Edita las imágenes que aparecen en la sección "Descubre"
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categories.map((category) => (
                    <Card key={category.id} className="overflow-hidden">
                      <div className="relative aspect-[4/3] bg-muted">
                        {category.image ? (
                          <img
                            src={category.image}
                            alt={category.name.es}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                            Sin imagen
                          </div>
                        )}
                      </div>
                      <CardContent className="p-4 space-y-3">
                        <h3 className="font-semibold text-lg">{category.name.es}</h3>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => handleEditCategory(category)}
                        >
                          <ImageIcon className="h-4 w-4 mr-2" />
                          Editar imagen
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NEW: Availability Tab */}
          <TabsContent value="availability">
            <Card>
              <CardHeader>
                <CardTitle>Gestión de Disponibilidad</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Marca productos como disponibles o fuera de servicio temporalmente
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      className="flex items-center gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="w-16 h-16 flex items-center justify-center bg-white rounded p-1">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-full h-full object-contain object-center"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{product.name}</h3>
                        <p className="text-sm text-muted-foreground">
                          RD${product.price.toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {product.available ? (
                          <Badge variant="default" className="bg-green-500">
                            Disponible
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            Fuera de servicio
                          </Badge>
                        )}
                        <Switch
                          checked={product.available}
                          onCheckedChange={() => handleToggleAvailability(product.id, product.available)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NEW: Search Config Tab */}
          <TabsContent value="search">
            <div className="space-y-6">
              {/* Search Hero Configuration */}
              <Card>
                <CardHeader>
                  <CardTitle>Configuración Visual de Búsqueda</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Personaliza la experiencia visual de búsqueda tipo Apple
                  </p>
                </CardHeader>
                <CardContent>
                  {searchConfig && (
                    <div className="space-y-6">
                      <div className="space-y-2">
                        <Label>Imagen Hero (URL)</Label>
                        <Input
                          value={searchConfig.heroImage}
                          onChange={(e) => setSearchConfig({ ...searchConfig, heroImage: e.target.value })}
                          placeholder="https://images.unsplash.com/..."
                        />
                        <p className="text-xs text-muted-foreground">
                          Imagen grande de fondo (1920x600px recomendado)
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>Texto Principal (Headline)</Label>
                        <Input
                          value={searchConfig.headline}
                          onChange={(e) => setSearchConfig({ ...searchConfig, headline: e.target.value })}
                          placeholder="Descubre lo extraordinario"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Subtítulo</Label>
                        <Input
                          value={searchConfig.subtitle}
                          onChange={(e) => setSearchConfig({ ...searchConfig, subtitle: e.target.value })}
                          placeholder="Calidad y estilo para cada espacio"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Vista Previa</Label>
                        <div className="relative h-48 rounded-lg overflow-hidden border">
                          <img
                            src={searchConfig.heroImage}
                            alt="Preview"
                            className="w-full h-full object-cover"
                            onError={(e) => e.target.src = 'https://via.placeholder.com/1920x600?text=Error+cargando+imagen'}
                          />
                          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/70 flex flex-col items-center justify-center text-center p-6">
                            <h2 className="text-3xl font-light text-white mb-2">
                              {searchConfig.headline}
                            </h2>
                            <p className="text-lg text-white/90 font-light">
                              {searchConfig.subtitle}
                            </p>
                          </div>
                        </div>
                      </div>

                      <Button onClick={handleSaveSearchConfig} className="w-full">
                        <Save className="h-4 w-4 mr-2" />
                        Guardar Configuración
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Search Recommendations */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Recomendaciones del Buscador</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        Gestiona las sugerencias que aparecen al abrir el buscador
                      </p>
                    </div>
                    <Button onClick={handleCreateRecommendation}>
                      <ImagePlus className="h-4 w-4 mr-2" />
                      Nueva Recomendación
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {searchRecommendations.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No hay recomendaciones. Crea la primera.
                      </div>
                    ) : (
                      searchRecommendations
                        .sort((a, b) => a.order - b.order)
                        .map((rec) => (
                          <Card key={rec.id} className="overflow-hidden">
                            <div className="flex items-center gap-4 p-4">
                              {/* Image Preview */}
                              <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-muted">
                                <img
                                  src={rec.imageUrl}
                                  alt={rec.name}
                                  className="w-full h-full object-cover"
                                  onError={(e) => e.target.src = 'https://via.placeholder.com/200?text=Error'}
                                />
                              </div>

                              {/* Info */}
                              <div className="flex-1">
                                <h3 className="font-semibold text-lg">{rec.name}</h3>
                                <p className="text-sm text-muted-foreground">
                                  Destino: {rec.destination}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  Orden: {rec.order}
                                </p>
                              </div>

                              {/* Status Badge */}
                              <div className="flex-shrink-0">
                                <Badge variant={rec.active ? "default" : "secondary"}>
                                  {rec.active ? "Activo" : "Inactivo"}
                                </Badge>
                              </div>

                              {/* Actions */}
                              <div className="flex-shrink-0 flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleEditRecommendation(rec)}
                                >
                                  Editar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteRecommendation(rec.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </Card>
                        ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* NEW: Hero Blocks Tab */}
          <TabsContent value="hero">
            <Card>
              <CardHeader>
                <CardTitle>Bloques Hero - Imágenes Grandes</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Administra las imágenes grandes tipo hero/banner para secciones destacadas
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {heroBlocks.map((block) => (
                    <Card key={block.id} className="overflow-hidden">
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Left: Preview */}
                        <div className="relative" style={{ height: '300px' }}>
                          <div
                            className="w-full h-full"
                            style={{
                              backgroundImage: `url(${block.imageUrl})`,
                              backgroundPosition: block.position || 'center center',
                              backgroundSize: 'cover',
                              backgroundRepeat: 'no-repeat'
                            }}
                          >
                            {block.overlay && (
                              <div 
                                className="absolute inset-0 bg-black"
                                style={{ opacity: block.overlayOpacity || 0.4 }}
                              />
                            )}
                            <div 
                              className="absolute inset-0 flex flex-col items-center justify-center p-6"
                              style={{ textAlign: block.textAlign || 'center' }}
                            >
                              {block.title && (
                                <h2 className="text-3xl font-bold text-white mb-2">
                                  {block.title}
                                </h2>
                              )}
                              {block.subtitle && (
                                <p className="text-lg text-white/90">
                                  {block.subtitle}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Right: Info */}
                        <CardContent className="p-6">
                          <div className="space-y-3">
                            <div>
                              <h3 className="font-semibold text-lg mb-1">{block.name}</h3>
                              <p className="text-xs text-muted-foreground">ID: {block.id}</p>
                            </div>

                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Altura:</span>
                                <span className="font-medium">{block.height}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Posición:</span>
                                <span className="font-medium">{block.position}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Overlay:</span>
                                <span className="font-medium">
                                  {block.overlay ? `Sí (${Math.round(block.overlayOpacity * 100)}%)` : 'No'}
                                </span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Estado:</span>
                                <Badge variant={block.enabled ? 'default' : 'secondary'}>
                                  {block.enabled ? 'Activo' : 'Inactivo'}
                                </Badge>
                              </div>
                            </div>

                            <Button
                              className="w-full mt-4"
                              onClick={() => handleEditHeroBlock(block)}
                            >
                              <ImagePlus className="h-4 w-4 mr-2" />
                              Editar Bloque Hero
                            </Button>
                          </div>
                        </CardContent>
                      </div>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NEW: Fondos (Landing Backgrounds) Tab */}
          <TabsContent value="fondos">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Fondos - Páginas de Promoción</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Gestiona las landing pages estilo Apple para cada categoría
                    </p>
                  </div>
                  <Button onClick={handleCreateLandingConfig}>
                    <ImagePlus className="h-4 w-4 mr-2" />
                    Nueva Configuración
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {landingConfigs.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No hay configuraciones. Crea la primera.
                    </div>
                  ) : (
                    landingConfigs.map((config) => (
                      <Card key={config.id} className="overflow-hidden">
                        <div className="flex items-center gap-4 p-4">
                          {/* Product Image Preview */}
                          <div className="w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                            {config.productImage ? (
                              <img
                                src={config.productImage}
                                alt={config.name}
                                className="w-full h-full object-contain"
                                onError={(e) => e.target.src = 'https://via.placeholder.com/200?text=Error'}
                              />
                            ) : (
                              <p className="text-xs text-muted-foreground">Sin imagen</p>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1">
                            <h3 className="font-bold text-xl">{config.name}</h3>
                            <p className="text-lg text-muted-foreground">{config.title}</p>
                            <p className="text-sm text-muted-foreground mt-1">{config.subtitle}</p>
                            <div className="flex items-center gap-4 mt-2">
                              <span className="font-bold text-primary">
                                RD${config.price?.toLocaleString()}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                Desde RD${config.monthlyPrice?.toLocaleString()}/mes
                              </span>
                            </div>
                          </div>

                          {/* Status Badge */}
                          <div className="flex-shrink-0">
                            <Badge variant={config.active ? "default" : "secondary"}>
                              {config.active ? "Activo" : "Inactivo"}
                            </Badge>
                          </div>

                          {/* Actions */}
                          <div className="flex-shrink-0 flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditLandingConfig(config)}
                            >
                              Editar
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteLandingConfig(config.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NEW: Portadas Tab */}
          <TabsContent value="portadas">
            <Card>
              <CardHeader>
                <CardTitle>Portadas - Páginas Principales</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Edita las portadas de Home, Destacados, Catálogo y Contacto
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {portadas.map((portada) => {
                    const labels = {
                      home: 'Inicio',
                      featured: 'Destacados',
                      catalog: 'Catálogo',
                      contact: 'Contacto'
                    };
                    return (
                      <Card key={portada.id} className="overflow-hidden">
                        <div className="flex items-center gap-4 p-4">
                          {/* Image Preview */}
                          <div className="w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-muted">
                            {portada.imageUrl ? (
                              <img
                                src={portada.imageUrl}
                                alt={portada.title}
                                className="w-full h-full object-cover"
                                onError={(e) => e.target.src = 'https://via.placeholder.com/400x200?text=Error'}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                                Sin imagen
                              </div>
                            )}
                          </div>

                          {/* Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge variant="outline">
                                {labels[portada.id] || portada.id}
                              </Badge>
                              <Badge variant={portada.active ? "default" : "secondary"}>
                                {portada.active ? "Activo" : "Inactivo"}
                              </Badge>
                            </div>
                            <h3 className="font-bold text-xl">{portada.title}</h3>
                            <p className="text-sm text-muted-foreground mt-1">{portada.subtitle}</p>
                          </div>

                          {/* Actions */}
                          <div className="flex-shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEditPortada(portada)}
                            >
                              <Save className="h-4 w-4 mr-2" />
                              Editar
                            </Button>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NEW: Logo Tab */}
          <TabsContent value="logo">
            <Card>
              <CardHeader>
                <CardTitle>Logo</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Configura el logo de NAGOC GROUP
                </p>
              </CardHeader>
              <CardContent>
                {logo && (
                  <Card className="overflow-hidden">
                    <div className="flex items-center gap-6 p-6">
                      {/* Logo Preview */}
                      <div className="w-64 h-32 flex-shrink-0 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                        {logo.logoUrl ? (
                          <img
                            src={logo.logoUrl}
                            alt={logo.altText}
                            className="max-w-full max-h-full object-contain"
                            onError={(e) => e.target.src = 'https://via.placeholder.com/200x80?text=LOGO'}
                          />
                        ) : (
                          <div className="text-xs text-muted-foreground">
                            Sin logo
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline">Logo Principal</Badge>
                          <Badge variant={logo.active ? "default" : "secondary"}>
                            {logo.active ? "Activo" : "Inactivo"}
                          </Badge>
                        </div>
                        <h3 className="font-bold text-lg">{logo.altText}</h3>
                        <p className="text-sm text-muted-foreground mt-1 break-all">
                          {logo.logoUrl || 'Sin URL configurada'}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleEditLogo}
                        >
                          <Save className="h-4 w-4 mr-2" />
                          Editar
                        </Button>
                      </div>
                    </div>
                  </Card>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Product Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Producto</DialogTitle>
          </DialogHeader>
          {editingProduct && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre</Label>
                <Input
                  value={editingProduct.name}
                  onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Input
                  value={editingProduct.description}
                  onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Precio</Label>
                  <Input
                    type="number"
                    value={editingProduct.price}
                    onChange={(e) => setEditingProduct({ ...editingProduct, price: parseFloat(e.target.value) })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Cuota Mensual</Label>
                  <Input
                    type="number"
                    value={editingProduct.monthly_payment}
                    onChange={(e) => setEditingProduct({ ...editingProduct, monthly_payment: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>URL de Imagen</Label>
                <Input
                  value={editingProduct.image}
                  onChange={(e) => setEditingProduct({ ...editingProduct, image: e.target.value })}
                  placeholder="https://images.unsplash.com/..."
                />
              </div>
              <div className="space-y-2">
                <Label>Vista previa</Label>
                <div className="w-full h-48 flex items-center justify-center bg-white rounded-lg p-4">
                  <img
                    src={editingProduct.image}
                    alt="Preview"
                    className="w-full h-full object-contain object-center"
                    onError={(e) => e.target.src = 'https://via.placeholder.com/400x300?text=Error+cargando+imagen'}
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveProduct}>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar cambios
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Categoría: {editingCategory?.name.es}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Personaliza la experiencia premium de esta categoría
            </p>
          </DialogHeader>
          {editingCategory && (
            <div className="space-y-6">
              {/* Card Image (for catalog) */}
              <div className="space-y-2">
                <Label>Imagen de Tarjeta (Catálogo)</Label>
                <Input
                  value={editingCategory.image || ''}
                  onChange={(e) => setEditingCategory({ ...editingCategory, image: e.target.value })}
                  placeholder="https://images.unsplash.com/..."
                />
              </div>

              <Separator />

              {/* Hero Image (for landing page) */}
              <div className="space-y-2">
                <Label>Imagen Hero (Página de Categoría)</Label>
                <Input
                  value={editingCategory.heroImage || ''}
                  onChange={(e) => setEditingCategory({ ...editingCategory, heroImage: e.target.value })}
                  placeholder="https://images.unsplash.com/..."
                />
                <p className="text-xs text-muted-foreground">
                  Imagen grande para la página específica de esta categoría
                </p>
              </div>

              {/* Headline */}
              <div className="space-y-2">
                <Label>Título Principal (Español)</Label>
                <Input
                  value={editingCategory.headline?.es || ''}
                  onChange={(e) => setEditingCategory({ 
                    ...editingCategory, 
                    headline: { ...editingCategory.headline, es: e.target.value }
                  })}
                  placeholder="Experimenta lo extraordinario"
                />
              </div>

              {/* Subtitle */}
              <div className="space-y-2">
                <Label>Subtítulo (Español)</Label>
                <Input
                  value={editingCategory.subtitle?.es || ''}
                  onChange={(e) => setEditingCategory({ 
                    ...editingCategory, 
                    subtitle: { ...editingCategory.subtitle, es: e.target.value }
                  })}
                  placeholder="Tecnología visual que transforma tu espacio"
                />
              </div>

              {/* Preview */}
              <div className="space-y-2">
                <Label>Vista Previa de Página de Categoría</Label>
                <div className="relative h-64 bg-muted rounded-lg overflow-hidden">
                  {editingCategory.heroImage ? (
                    <>
                      <img
                        src={editingCategory.heroImage}
                        alt="Preview"
                        className="w-full h-full object-cover"
                        onError={(e) => e.target.src = 'https://via.placeholder.com/1920x800?text=Error+cargando+imagen'}
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/60 flex flex-col items-center justify-center text-center p-6">
                        <h2 className="text-3xl font-bold text-white mb-2">
                          {editingCategory.headline?.es || editingCategory.name.es}
                        </h2>
                        <p className="text-lg text-white/90">
                          {editingCategory.subtitle?.es || ''}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      Vista previa de imagen hero
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setCategoryDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveCategory}>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar cambios
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Hero Block Dialog */}
      <Dialog open={heroDialogOpen} onOpenChange={setHeroDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Bloque Hero: {editingHeroBlock?.name}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Personaliza la imagen grande y configuración visual
            </p>
          </DialogHeader>
          {editingHeroBlock && (
            <div className="space-y-6">
              {/* Image URL */}
              <div className="space-y-2">
                <Label>URL de Imagen *</Label>
                <Input
                  value={editingHeroBlock.imageUrl}
                  onChange={(e) => setEditingHeroBlock({ ...editingHeroBlock, imageUrl: e.target.value })}
                  placeholder="https://images.unsplash.com/..."
                />
                <p className="text-xs text-muted-foreground">
                  Imagen grande para el bloque hero
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Title */}
                <div className="space-y-2">
                  <Label>Título</Label>
                  <Input
                    value={editingHeroBlock.title || ''}
                    onChange={(e) => setEditingHeroBlock({ ...editingHeroBlock, title: e.target.value })}
                    placeholder="Descubre lo extraordinario"
                  />
                </div>

                {/* Subtitle */}
                <div className="space-y-2">
                  <Label>Subtítulo</Label>
                  <Input
                    value={editingHeroBlock.subtitle || ''}
                    onChange={(e) => setEditingHeroBlock({ ...editingHeroBlock, subtitle: e.target.value })}
                    placeholder="Tecnología premium para tu hogar"
                  />
                </div>
              </div>

              <Separator />

              {/* Advanced Options */}
              <div className="space-y-4">
                <h4 className="font-semibold">Opciones Avanzadas</h4>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Background Position */}
                  <div className="space-y-2">
                    <Label>Posición de Imagen</Label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={editingHeroBlock.position}
                      onChange={(e) => setEditingHeroBlock({ ...editingHeroBlock, position: e.target.value })}
                    >
                      <option value="center center">Centro</option>
                      <option value="top center">Arriba Centro</option>
                      <option value="bottom center">Abajo Centro</option>
                      <option value="center left">Centro Izquierda</option>
                      <option value="center right">Centro Derecha</option>
                    </select>
                  </div>

                  {/* Height */}
                  <div className="space-y-2">
                    <Label>Altura del Bloque</Label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={editingHeroBlock.height}
                      onChange={(e) => setEditingHeroBlock({ ...editingHeroBlock, height: e.target.value })}
                    >
                      <option value="400px">Pequeña (400px)</option>
                      <option value="500px">Mediana (500px)</option>
                      <option value="600px">Grande (600px)</option>
                      <option value="800px">Extra Grande (800px)</option>
                      <option value="100vh">Pantalla Completa</option>
                    </select>
                  </div>

                  {/* Text Align */}
                  <div className="space-y-2">
                    <Label>Alineación de Texto</Label>
                    <select
                      className="w-full px-3 py-2 border rounded-md"
                      value={editingHeroBlock.textAlign}
                      onChange={(e) => setEditingHeroBlock({ ...editingHeroBlock, textAlign: e.target.value })}
                    >
                      <option value="center">Centro</option>
                      <option value="left">Izquierda</option>
                      <option value="right">Derecha</option>
                    </select>
                  </div>

                  {/* Overlay Opacity */}
                  <div className="space-y-2">
                    <Label>Opacidad del Overlay ({Math.round(editingHeroBlock.overlayOpacity * 100)}%)</Label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={editingHeroBlock.overlayOpacity}
                      onChange={(e) => setEditingHeroBlock({ ...editingHeroBlock, overlayOpacity: parseFloat(e.target.value) })}
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Overlay Toggle */}
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="overlay"
                    checked={editingHeroBlock.overlay}
                    onChange={(e) => setEditingHeroBlock({ ...editingHeroBlock, overlay: e.target.checked })}
                  />
                  <Label htmlFor="overlay">Activar overlay oscuro</Label>
                </div>
              </div>

              <Separator />

              {/* Live Preview */}
              <div className="space-y-2">
                <Label>Vista Previa en Tiempo Real</Label>
                <div 
                  className="relative rounded-lg overflow-hidden"
                  style={{ height: editingHeroBlock.height }}
                >
                  <div
                    className="w-full h-full"
                    style={{
                      backgroundImage: `url(${editingHeroBlock.imageUrl})`,
                      backgroundPosition: editingHeroBlock.position,
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat'
                    }}
                  >
                    {editingHeroBlock.overlay && (
                      <div 
                        className="absolute inset-0 bg-black"
                        style={{ opacity: editingHeroBlock.overlayOpacity }}
                      />
                    )}
                    <div 
                      className="absolute inset-0 flex flex-col items-center justify-center p-6"
                      style={{ textAlign: editingHeroBlock.textAlign }}
                    >
                      {editingHeroBlock.title && (
                        <h2 className="text-4xl font-bold text-white mb-2">
                          {editingHeroBlock.title}
                        </h2>
                      )}
                      {editingHeroBlock.subtitle && (
                        <p className="text-xl text-white/90">
                          {editingHeroBlock.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setHeroDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveHeroBlock}>
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Cambios
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Search Recommendation Dialog */}
      <Dialog open={recommendationDialogOpen} onOpenChange={setRecommendationDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {searchRecommendations.find(r => r.id === editingRecommendation?.id) 
                ? 'Editar Recomendación' 
                : 'Nueva Recomendación'}
            </DialogTitle>
          </DialogHeader>
          {editingRecommendation && (
            <div className="space-y-4">
              {/* Name */}
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={editingRecommendation.name}
                  onChange={(e) => setEditingRecommendation({ ...editingRecommendation, name: e.target.value })}
                  placeholder="Televisores"
                />
              </div>

              {/* Image URL */}
              <div className="space-y-2">
                <Label>URL de Imagen *</Label>
                <Input
                  value={editingRecommendation.imageUrl}
                  onChange={(e) => setEditingRecommendation({ ...editingRecommendation, imageUrl: e.target.value })}
                  placeholder="https://images.unsplash.com/..."
                />
              </div>

              {/* Destination */}
              <div className="space-y-2">
                <Label>Destino (URL o slug) *</Label>
                <Input
                  value={editingRecommendation.destination}
                  onChange={(e) => setEditingRecommendation({ ...editingRecommendation, destination: e.target.value })}
                  placeholder="/recommendation/rec_televisores"
                />
                <p className="text-xs text-muted-foreground">
                  Usa /recommendation/{editingRecommendation.id} para mostrar la landing page personalizada
                </p>
              </div>

              <Separator />

              {/* Landing Page - Featured Product Promotion */}
              <div className="space-y-4">
                <h4 className="font-semibold text-lg">🎯 Promoción de Producto Protagonista (Estilo Apple)</h4>
                <p className="text-sm text-muted-foreground">
                  Configura una landing premium con producto destacado estilo Vision Pro
                </p>
                
                {/* Product Image (Protagonist) */}
                <div className="space-y-2">
                  <Label>Imagen del Producto Protagonista *</Label>
                  <Input
                    value={editingRecommendation.productImage || ''}
                    onChange={(e) => setEditingRecommendation({ ...editingRecommendation, productImage: e.target.value })}
                    placeholder="https://images.unsplash.com/..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Imagen grande del producto que será el protagonista visual (PNG con fondo transparente recomendado)
                  </p>
                </div>

                {/* Background Image */}
                <div className="space-y-2">
                  <Label>Imagen de Fondo (Opcional)</Label>
                  <Input
                    value={editingRecommendation.heroImage || ''}
                    onChange={(e) => setEditingRecommendation({ ...editingRecommendation, heroImage: e.target.value })}
                    placeholder="https://images.unsplash.com/..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Fondo elegante para la landing (se mostrará con opacidad baja)
                  </p>
                </div>

                {/* Background Color */}
                <div className="space-y-2">
                  <Label>Color de Fondo</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={editingRecommendation.backgroundColor || '#f5f5f7'}
                      onChange={(e) => setEditingRecommendation({ ...editingRecommendation, backgroundColor: e.target.value })}
                      className="w-20 h-10"
                    />
                    <Input
                      value={editingRecommendation.backgroundColor || '#f5f5f7'}
                      onChange={(e) => setEditingRecommendation({ ...editingRecommendation, backgroundColor: e.target.value })}
                      placeholder="#f5f5f7"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Color de fondo principal (blanco: #ffffff, negro: #1d1d1f, gris claro: #f5f5f7)
                  </p>
                </div>

                {/* Title */}
                <div className="space-y-2">
                  <Label>Título Principal *</Label>
                  <Input
                    value={editingRecommendation.title || ''}
                    onChange={(e) => setEditingRecommendation({ ...editingRecommendation, title: e.target.value })}
                    placeholder='TCL 55" Smart TV'
                  />
                </div>

                {/* Subtitle */}
                <div className="space-y-2">
                  <Label>Subtítulo</Label>
                  <Input
                    value={editingRecommendation.subtitle || ''}
                    onChange={(e) => setEditingRecommendation({ ...editingRecommendation, subtitle: e.target.value })}
                    placeholder="Experiencia cinematográfica en tu hogar"
                  />
                </div>

                {/* CTA Buy Button */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Texto Botón Comprar</Label>
                    <Input
                      value={editingRecommendation.ctaBuyText || 'Comprar ahora'}
                      onChange={(e) => setEditingRecommendation({ ...editingRecommendation, ctaBuyText: e.target.value })}
                      placeholder="Comprar ahora"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Link Botón Comprar</Label>
                    <Input
                      value={editingRecommendation.ctaBuyLink || ''}
                      onChange={(e) => setEditingRecommendation({ ...editingRecommendation, ctaBuyLink: e.target.value })}
                      placeholder="/catalog?category=televisores"
                    />
                  </div>
                </div>

                {/* CTA Info Button */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Texto Botón Información</Label>
                    <Input
                      value={editingRecommendation.ctaInfoText || 'Más información'}
                      onChange={(e) => setEditingRecommendation({ ...editingRecommendation, ctaInfoText: e.target.value })}
                      placeholder="Ver detalles"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Link Botón Información</Label>
                    <Input
                      value={editingRecommendation.ctaInfoLink || ''}
                      onChange={(e) => setEditingRecommendation({ ...editingRecommendation, ctaInfoLink: e.target.value })}
                      placeholder="/catalog?category=televisores"
                    />
                  </div>
                </div>

                {/* Premium Preview */}
                {editingRecommendation.productImage && (
                  <div className="space-y-2">
                    <Label>Vista Previa Premium</Label>
                    <div 
                      className="relative h-80 rounded-xl overflow-hidden border-2"
                      style={{
                        backgroundColor: editingRecommendation.backgroundColor || '#f5f5f7'
                      }}
                    >
                      {/* Background Image */}
                      {editingRecommendation.heroImage && (
                        <div
                          className="absolute inset-0 opacity-20"
                          style={{
                            backgroundImage: `url(${editingRecommendation.heroImage})`,
                            backgroundPosition: 'center',
                            backgroundSize: 'cover'
                          }}
                        />
                      )}
                      
                      {/* Content */}
                      <div className="relative h-full grid grid-cols-2 items-center gap-4 p-6">
                        {/* Product Image */}
                        <div className="flex items-center justify-center">
                          <img
                            src={editingRecommendation.productImage}
                            alt="Product"
                            className="w-full h-auto object-contain max-h-60 drop-shadow-xl"
                            onError={(e) => e.target.src = 'https://via.placeholder.com/400?text=Producto'}
                          />
                        </div>
                        
                        {/* Text */}
                        <div className="space-y-3">
                          {editingRecommendation.title && (
                            <h3 
                              className="text-2xl font-bold line-clamp-2"
                              style={{
                                color: editingRecommendation.backgroundColor === '#ffffff' || editingRecommendation.backgroundColor === '#f5f5f7' ? '#1d1d1f' : '#f5f5f7'
                              }}
                            >
                              {editingRecommendation.title}
                            </h3>
                          )}
                          {editingRecommendation.subtitle && (
                            <p 
                              className="text-sm line-clamp-2"
                              style={{
                                color: editingRecommendation.backgroundColor === '#ffffff' || editingRecommendation.backgroundColor === '#f5f5f7' ? '#6e6e73' : '#d1d1d6'
                              }}
                            >
                              {editingRecommendation.subtitle}
                            </p>
                          )}
                          <div className="flex gap-2 pt-2">
                            {editingRecommendation.ctaBuyText && (
                              <div className="px-4 py-2 bg-primary text-primary-foreground rounded-full text-xs font-semibold">
                                {editingRecommendation.ctaBuyText}
                              </div>
                            )}
                            {editingRecommendation.ctaInfoText && (
                              <div 
                                className="px-4 py-2 border-2 rounded-full text-xs font-semibold"
                                style={{
                                  borderColor: editingRecommendation.backgroundColor === '#ffffff' || editingRecommendation.backgroundColor === '#f5f5f7' ? '#1d1d1f' : '#f5f5f7',
                                  color: editingRecommendation.backgroundColor === '#ffffff' || editingRecommendation.backgroundColor === '#f5f5f7' ? '#1d1d1f' : '#f5f5f7'
                                }}
                              >
                                {editingRecommendation.ctaInfoText}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                {/* Order */}
                <div className="space-y-2">
                  <Label>Orden de Visualización</Label>
                  <Input
                    type="number"
                    value={editingRecommendation.order}
                    onChange={(e) => setEditingRecommendation({ ...editingRecommendation, order: parseInt(e.target.value) || 0 })}
                    placeholder="1"
                  />
                </div>

                {/* Active Status */}
                <div className="space-y-2">
                  <Label>Estado</Label>
                  <div className="flex items-center space-x-2 pt-2">
                    <Switch
                      checked={editingRecommendation.active}
                      onCheckedChange={(checked) => setEditingRecommendation({ ...editingRecommendation, active: checked })}
                    />
                    <span className="text-sm">
                      {editingRecommendation.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="space-y-2">
                <Label>Vista Previa</Label>
                <div className="flex items-center gap-4 p-4 rounded-lg border bg-muted/30">
                  <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-white">
                    <img
                      src={editingRecommendation.imageUrl || 'https://via.placeholder.com/200'}
                      alt="Preview"
                      className="w-full h-full object-cover"
                      onError={(e) => e.target.src = 'https://via.placeholder.com/200?text=Error'}
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">
                      {editingRecommendation.name || 'Nombre de la recomendación'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {editingRecommendation.destination || 'Destino'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setRecommendationDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveRecommendation}
                  disabled={!editingRecommendation.name || !editingRecommendation.imageUrl || !editingRecommendation.destination}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Recomendación
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Landing Config Dialog (Fondos) */}
      <Dialog open={landingConfigDialogOpen} onOpenChange={setLandingConfigDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {landingConfigs.find(c => c.id === editingLandingConfig?.id) 
                ? 'Editar Configuración de Fondo' 
                : 'Nueva Configuración de Fondo'}
            </DialogTitle>
          </DialogHeader>
          {editingLandingConfig && (
            <div className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={editingLandingConfig.name}
                  onChange={(e) => setEditingLandingConfig({ ...editingLandingConfig, name: e.target.value })}
                  placeholder="Televisores"
                />
              </div>

              <Separator />

              {/* Title */}
              <div className="space-y-2">
                <Label>Título Principal *</Label>
                <Input
                  value={editingLandingConfig.title}
                  onChange={(e) => setEditingLandingConfig({ ...editingLandingConfig, title: e.target.value })}
                  placeholder='TCL 55" Smart TV 4K'
                />
              </div>

              {/* Subtitle */}
              <div className="space-y-2">
                <Label>Subtítulo</Label>
                <Input
                  value={editingLandingConfig.subtitle}
                  onChange={(e) => setEditingLandingConfig({ ...editingLandingConfig, subtitle: e.target.value })}
                  placeholder="Experiencia cinematográfica con tecnología QLED"
                />
              </div>

              <Separator />

              {/* Pricing */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Precio (RD$) *</Label>
                  <Input
                    type="number"
                    value={editingLandingConfig.price}
                    onChange={(e) => setEditingLandingConfig({ ...editingLandingConfig, price: parseFloat(e.target.value) || 0 })}
                    placeholder="55990"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Precio Mensual (RD$/mes) *</Label>
                  <Input
                    type="number"
                    value={editingLandingConfig.monthlyPrice}
                    onChange={(e) => setEditingLandingConfig({ ...editingLandingConfig, monthlyPrice: parseFloat(e.target.value) || 0 })}
                    placeholder="5690"
                  />
                </div>
              </div>

              <Separator />

              {/* Images */}
              <div className="space-y-4">
                <h4 className="font-semibold">Imágenes</h4>
                
                {/* Product Image */}
                <div className="space-y-2">
                  <Label>URL Imagen del Producto (Protagonista) *</Label>
                  <Input
                    value={editingLandingConfig.productImage}
                    onChange={(e) => setEditingLandingConfig({ ...editingLandingConfig, productImage: e.target.value })}
                    placeholder="https://images.unsplash.com/..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Imagen del producto que dominará la pantalla (PNG transparente recomendado)
                  </p>
                </div>

                {/* Background Image */}
                <div className="space-y-2">
                  <Label>URL Imagen de Fondo *</Label>
                  <Input
                    value={editingLandingConfig.backgroundImage}
                    onChange={(e) => setEditingLandingConfig({ ...editingLandingConfig, backgroundImage: e.target.value })}
                    placeholder="https://images.unsplash.com/..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Fondo elegante (se aplicará blur automático para estilo Apple)
                  </p>
                </div>
              </div>

              <Separator />

              {/* Product ID Link */}
              <div className="space-y-2">
                <Label>ID del Producto (Opcional)</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={editingLandingConfig.productId || ''}
                  onChange={(e) => setEditingLandingConfig({ ...editingLandingConfig, productId: e.target.value })}
                >
                  <option value="">-- Ninguno (mostrar catálogo) --</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} - {p.brand}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  Selecciona el producto al que redirigirán los botones "Comprar" e "Información"
                </p>
              </div>

              <Separator />

              {/* Preview */}
              {editingLandingConfig.productImage && (
                <div className="space-y-2">
                  <Label>Vista Previa</Label>
                  <div 
                    className="relative h-96 rounded-xl overflow-hidden"
                    style={{
                      backgroundImage: `url(${editingLandingConfig.backgroundImage})`,
                      backgroundPosition: 'center',
                      backgroundSize: 'cover',
                      filter: 'blur(20px) brightness(0.7)'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
                      <img
                        src={editingLandingConfig.productImage}
                        alt="Preview"
                        className="max-h-48 object-contain drop-shadow-2xl mb-4"
                        onError={(e) => e.target.src = 'https://via.placeholder.com/400?text=Producto'}
                      />
                      {editingLandingConfig.title && (
                        <h2 className="text-3xl font-bold text-white mb-2">
                          {editingLandingConfig.title}
                        </h2>
                      )}
                      {editingLandingConfig.subtitle && (
                        <p className="text-lg text-white/90 mb-4">
                          {editingLandingConfig.subtitle}
                        </p>
                      )}
                      {editingLandingConfig.price > 0 && (
                        <div className="mb-4">
                          <div className="text-4xl font-bold text-white">
                            RD${editingLandingConfig.price.toLocaleString()}
                          </div>
                          {editingLandingConfig.monthlyPrice > 0 && (
                            <div className="text-lg text-white/70">
                              Desde RD${editingLandingConfig.monthlyPrice.toLocaleString()}/mes
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <div className="px-6 py-2 bg-white text-black rounded-full text-sm font-semibold">
                          Comprar
                        </div>
                        <div className="px-6 py-2 border-2 border-white text-white rounded-full text-sm font-semibold">
                          Información
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <Separator />

              {/* Active Status */}
              <div className="flex items-center space-x-2">
                <Switch
                  checked={editingLandingConfig.active}
                  onCheckedChange={(checked) => setEditingLandingConfig({ ...editingLandingConfig, active: checked })}
                />
                <Label>
                  {editingLandingConfig.active ? 'Activo' : 'Inactivo'}
                </Label>
              </div>

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setLandingConfigDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveLandingConfig}
                  disabled={!editingLandingConfig.name || !editingLandingConfig.title || !editingLandingConfig.productImage || !editingLandingConfig.backgroundImage}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Configuración
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Portada Edit Dialog */}
      <Dialog open={portadaDialogOpen} onOpenChange={setPortadaDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Editar Portada - {
                editingPortada?.id === 'home' ? 'Inicio' :
                editingPortada?.id === 'featured' ? 'Destacados' :
                editingPortada?.id === 'catalog' ? 'Catálogo' :
                editingPortada?.id === 'contact' ? 'Contacto' :
                editingPortada?.id
              }
            </DialogTitle>
          </DialogHeader>
          {editingPortada && (
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Título *</Label>
                  <Input
                    value={editingPortada.title || ''}
                    onChange={(e) => setEditingPortada({ ...editingPortada, title: e.target.value })}
                    placeholder="Título principal"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Subtítulo</Label>
                  <Input
                    value={editingPortada.subtitle || ''}
                    onChange={(e) => setEditingPortada({ ...editingPortada, subtitle: e.target.value })}
                    placeholder="Subtítulo descriptivo"
                  />
                </div>

                <div className="space-y-2">
                  <Label>URL de Imagen *</Label>
                  <Input
                    value={editingPortada.imageUrl || ''}
                    onChange={(e) => setEditingPortada({ ...editingPortada, imageUrl: e.target.value })}
                    placeholder="https://images.unsplash.com/..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Imagen de fondo para la portada (se aplicará cover y centrado automático)
                  </p>
                </div>
              </div>

              <Separator />

              {/* Preview */}
              {editingPortada.imageUrl && (
                <div className="space-y-2">
                  <Label>Vista Previa</Label>
                  <div 
                    className="relative h-80 rounded-xl overflow-hidden"
                    style={{
                      backgroundImage: `url(${editingPortada.imageUrl})`,
                      backgroundPosition: 'center center',
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat'
                    }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
                      {editingPortada.title && (
                        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                          {editingPortada.title}
                        </h1>
                      )}
                      {editingPortada.subtitle && (
                        <p className="text-lg md:text-xl text-white/90 max-w-2xl">
                          {editingPortada.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setPortadaDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSavePortada}
                  disabled={!editingPortada.title || !editingPortada.imageUrl}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Portada
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Logo Edit Dialog */}
      <Dialog open={logoDialogOpen} onOpenChange={setLogoDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Logo</DialogTitle>
          </DialogHeader>
          {editingLogo && (
            <div className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>URL del Logo *</Label>
                  <Input
                    value={editingLogo.logoUrl || ''}
                    onChange={(e) => setEditingLogo({ ...editingLogo, logoUrl: e.target.value })}
                    placeholder="https://ejemplo.com/logo.png"
                  />
                  <p className="text-xs text-muted-foreground">
                    URL de la imagen del logo (PNG o SVG recomendado con fondo transparente)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Texto Alternativo</Label>
                  <Input
                    value={editingLogo.altText || ''}
                    onChange={(e) => setEditingLogo({ ...editingLogo, altText: e.target.value })}
                    placeholder="NAGOC GROUP"
                  />
                  <p className="text-xs text-muted-foreground">
                    Texto que aparece si la imagen no carga (para accesibilidad)
                  </p>
                </div>
              </div>

              <Separator />

              {/* Preview */}
              {editingLogo.logoUrl && (
                <div className="space-y-2">
                  <Label>Vista Previa</Label>
                  <div className="p-8 rounded-xl bg-muted flex items-center justify-center">
                    <img
                      src={editingLogo.logoUrl}
                      alt={editingLogo.altText}
                      className="max-h-32 object-contain"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setLogoDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleSaveLogo}
                  disabled={!editingLogo.logoUrl}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Logo
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboardNew;
